<template>
  <div class="w-full max-w-md rounded-lg bg-white font-sans shadow-sm">
    <div class="border-t border-gray-100"></div>

    <!-- Dynamic menu items -->
    <div v-for="(item, index) in filteredNavItems" :key="index">
      <div
        v-if="index > 0 && findMenuItemByAlias(item.type)?.visible"
        class="border-t border-gray-100"
      ></div>

      <!-- Language Dropdown -->
      <LanguageSelector
        v-if="item.type === 'changeLanguage' && (findMenuItemByAlias(item.type)?.visible ?? false)"
        :icon="findMenuItemByAlias(item.type)?.icon ?? ['fal', 'globe']"
        :title="item.title ?? ''"
        :selected-language="selectedLanguage"
        @language-change="handleLanguageChange"
      />

      <template v-else>
        <div
          v-if="findMenuItemByAlias(item.type)?.visible"
          class="flex h-[56px] cursor-pointer items-center justify-between px-4 hover:bg-gray-100"
          @click="handleMenuAction(item.type ?? '', item.url)"
        >
          <div class="flex items-center space-x-3">
            <div class="flex h-8 w-8 items-center justify-center">
              <font-awesome-icon
                v-if="findMenuItemByAlias(item.type)?.icon !== undefined"
                size="lg"
                class="w-5 shrink-0 text-gray-400"
                aria-hidden="true"
                :icon="findMenuItemByAlias(item.type)?.icon"
              />
              <div
                v-if="
                  findMenuItemByAlias(item.type)?.icon === undefined &&
                  findMenuItemByAlias(item.type)?.profilBadge !== undefined
                "
                class="flex h-8 w-8 items-center justify-center"
              >
                <AvatarComponent
                  :image="profileStore.profileImage"
                  :name="userStore.member.name"
                  :show-councellor="true"
                  avatar-class="h-10 w-10 cursor-pointer"
                />
              </div>
            </div>
            <span class="text-sm text-gray-700">{{ item.title }}</span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  useAppStore,
  useProgrammeStore,
  useLanguageStore,
  useUserStore,
  useAuthStore,
  useProfileStore,
  useNavigationStore
} from '@/stores'
import type { ILanguageItem } from '../../interfaces/ILanguageItem'
import type { SelectItem } from '@/interfaces/components/ISelectComponent'
import { Capacitor } from '@capacitor/core'
import { getLocaleBasedOnDeviceLocale } from '@/application/helpers/languageHelpers'
import router from '@/router'

interface IMenuItem {
  type: string
  icon?: [string, string]
  profilBadge?: boolean
  visible?: boolean
}

const programmeStore = useProgrammeStore()
const languageStore = useLanguageStore()
const authStore = useAuthStore()
const appStore = useAppStore()
const userStore = useUserStore()
const profileStore = useProfileStore()
const navigationStore = useNavigationStore()

onMounted(() => {
  const getLocale = Capacitor.isNativePlatform()
    ? getLocaleBasedOnDeviceLocale(languageStore.currentLocale)
    : languageStore.currentLocale

  const currentLocal = languageStore.listOfAvailableLanguages?.find(
    (item: ILanguageItem) => item.locale === getLocale
  )

  if (currentLocal) {
    selectedLanguage.value.name = currentLocal.name
    selectedLanguage.value.id = currentLocal.locale
  }
})

const emit = defineEmits<{
  'menu-action': [alias: string, link?: string]
  'language-change': [language: SelectItem]
  closeDrawer: []
}>()

const menuItems: IMenuItem[] = [
  {
    type: 'profileLabel',
    profilBadge: true,
    visible: true
  },
  {
    type: 'accountSettingsLabel',
    icon: ['fal', 'fa-gear'],
    visible: true
  },
  {
    type: 'changeLanguage',
    icon: ['fal', 'fa-globe'],
    visible:
      appStore.showLanguageSelector === true &&
      languageStore.listOfAvailableLanguages &&
      languageStore.listOfAvailableLanguages.length > 1
  },
  {
    type: 'switchProgram',
    icon: ['fal', 'fa-repeat'],
    visible: programmeStore.programmes.length > 1
  },
  {
    type: 'logOut',
    icon: ['fal', 'lock-open'],
    visible: true
  }
]

const selectedLanguage = ref<SelectItem>({ name: '', id: '' })

const filteredNavItems = computed(() =>
  (navigationStore.secondaryNavigation || []).filter((item) => item.title !== '')
)

const findMenuItemByAlias = (alias: string | undefined): IMenuItem | undefined => {
  return menuItems.find((item) => item.type === alias)
}

const handleLanguageChange = (language: SelectItem) => {
  emit('language-change', language)
}

const handleMenuAction = async (type: string, link?: string) => {
  switch (type) {
    case 'logOut':
      authStore.signOut()
      break
    case 'switchProgram':
      emit('closeDrawer')
      await programmeStore.resetSelectedProgramme()
      break
    case 'changeLanguage':
      break
    case 'profileLabel':
    case 'accountSettingsLabel':
      emit('closeDrawer')
      await router.push(link ?? '/')
      break
    default:
      console.log(`Action for ${type} not implemented`)
  }
}
</script>
